import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Banner, Button, Card, Spinner } from '../components/serac';
import MarketingHeader from '../components/common/MarketingHeader';
import MarketingFooter from '../components/common/MarketingFooter';
import toast from 'react-hot-toast';
import { getCsrfToken } from '../api/base';

interface DeviceSession {
  sessionId: string;
  machineInfo: string;
  createdAt: number;
  expiresAt: number;
  approved: boolean;
  denied: boolean;
}

export default function DeviceAuthorize() {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    portalSession,
    isPortalAuthenticated,
  } = useAuth();
  const [session, setSession] = useState<DeviceSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [authCode, setAuthCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const sessionId = searchParams.get('session');

  // Single auth model after the SI/customer demolition (2026-04-27):
  // only portal users can authorize CLI devices.
  const isAuthenticated = isPortalAuthenticated;
  const authToken = portalSession?.token;
  const isDirectUser = false;
  const isLicenseKeyAdmin = false;
  const canAuthorizeDevice = isPortalAuthenticated;

  // Helper to get the correct dashboard URL based on auth type
  const getDashboardUrl = () => {
    if (false) {
      return '/service-integrator/dashboard';
    }
    return '/portal/dashboard';
  };

  useEffect(() => {
    // Check if user is authenticated (either portal or enterprise)
    if (!isAuthenticated) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/?returnUrl=${returnUrl}`);
      return;
    }

    if (!sessionId) {
      toast.error(t('auth:device.missingSession'));
      navigate(getDashboardUrl());
      return;
    }

    // Fetch session info
    fetchSession();
  }, [sessionId, isAuthenticated]);

  async function fetchSession() {
    if (!sessionId) return;

    try {
      const response = await fetch(
        `/api/auth/device/session/${sessionId}`
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch session');
      }

      const data = await response.json();
      setSession(data.session);
    } catch (error: any) {
      console.error('Fetch session error:', error);
      toast.error(error.message || t('auth:device.fetchFailed'));
      navigate(getDashboardUrl());
    } finally {
      setIsLoading(false);
    }
  }

  async function handleApprove() {
    if (!sessionId) return;

    setIsProcessing(true);
    try {
      const response = await fetch(
        `/api/auth/device/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Fresh CLI-opened tab usually has no in-memory token; the
            // httpOnly portal_token cookie is sent automatically (same-origin)
            // and the backend accepts it. Send Bearer only if we do have one.
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
            // Browser POST rides the portal_token cookie, so CSRF protection
            // applies — echo the csrf_token cookie back in the header.
            ...(getCsrfToken() ? { 'X-CSRF-Token': getCsrfToken() as string } : {}),
          },
          body: JSON.stringify({
            sessionId,
            authType: false ? 'direct-user' :
                      false ? 'service-integrator' :
                      (false ? 'enterprise' : 'portal')
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        // POST /api/auth/device/approve has no subscription gate: device
        // authorization only links an already-authenticated portal account to
        // a local client, and Free is a full tier. The backend therefore never
        // sends billingUrl / trialExpired / subscriptionStatus, so there is no
        // billing refusal left to render here.
        throw new Error(error.error || t('auth:device.approveError'));
      }

      const data = await response.json();
      setAuthCode(data.authCode);
      toast.success(t('auth:device.approveSuccess'));
    } catch (error: any) {
      console.error('Approve error:', error);
      toast.error(error.message || t('auth:device.approveError'));
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleDeny() {
    if (!sessionId) return;

    setIsProcessing(true);
    try {
      const response = await fetch(
        `/api/auth/device/deny`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Fresh CLI-opened tab usually has no in-memory token; the
            // httpOnly portal_token cookie is sent automatically (same-origin)
            // and the backend accepts it. Send Bearer only if we do have one.
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
            // Browser POST rides the portal_token cookie, so CSRF protection
            // applies — echo the csrf_token cookie back in the header.
            ...(getCsrfToken() ? { 'X-CSRF-Token': getCsrfToken() as string } : {}),
          },
          body: JSON.stringify({ sessionId })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('auth:device.denyError'));
      }

      toast.success(t('auth:device.denySuccess'));
      setTimeout(() => {
        navigate(getDashboardUrl());
      }, 1500);
    } catch (error: any) {
      console.error('Deny error:', error);
      toast.error(error.message || t('auth:device.denyError'));
    } finally {
      setIsProcessing(false);
    }
  }

  function copyToClipboard() {
    if (!authCode) return;

    navigator.clipboard.writeText(authCode);
    setCopied(true);
    toast.success(t('auth:device.copySuccess'));

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <MarketingHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <Spinner size="lg" />
        </div>
        <MarketingFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <MarketingHeader />
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {t('auth:device.title')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            {t('auth:device.subtitle')}
          </p>
        </div>

        <Card>
          {!authCode ? (
            // Approval/Denial screen
            <div className="space-y-6">
              {/* Device info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('auth:device.deviceInfo')}</h3>
                <p className="text-sm text-gray-600">{session?.machineInfo || t('auth:device.unknownDevice')}</p>
              </div>

              {/* License key admin block - cannot use CLI */}
              {isLicenseKeyAdmin && (
                <Banner tone="warn" title={t('auth:device.licenseKeyBlock.title')}>
                  <p>
                    {t('auth:device.licenseKeyBlock.body1Before')}{' '}
                    <strong>{t('auth:device.licenseKeyBlock.body1Strong1')}</strong>{' '}
                    {t('auth:device.licenseKeyBlock.body1Or')}{' '}
                    <strong>{t('auth:device.licenseKeyBlock.body1Strong2')}</strong>{' '}
                    {t('auth:device.licenseKeyBlock.body1After')}
                  </p>
                  <p style={{ marginTop: 8 }}>
                    {t('auth:device.licenseKeyBlock.body2')}
                  </p>
                  <div style={{ marginTop: 12 }}>
                    <a href={false ? '/service-integrator/users' : '/enterprise/users'} className="text-sm font-medium underline">
                      {t('auth:device.licenseKeyBlock.link')}
                    </a>
                  </div>
                </Banner>
              )}

              {/* Warning - only show for users who can authorize */}
              {canAuthorizeDevice && (
                <Banner tone="warn" title={t('auth:device.warning.title')}>
                  {isDirectUser
                    ? t('auth:device.warning.directUser')
                    : t('auth:device.warning.portal')
                  }
                </Banner>
              )}

              {/* Permissions list - only show for users who can authorize */}
              {canAuthorizeDevice && (
              <>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">{t('auth:device.permissionsHeading')}</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {isDirectUser
                      ? t('auth:device.permissions.directAccess')
                      : t('auth:device.permissions.seracAccess')
                    }
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {isDirectUser
                      ? t('auth:device.permissions.directReadWrite')
                      : t('auth:device.permissions.seracReadWrite')
                    }
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('auth:device.permissions.mcpServer', { kind: isDirectUser ? t('auth:device.permissions.enterpriseKind') : '' })}
                  </li>
                </ul>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button
                  variant="quiet"
                  className="flex-1"
                  onClick={handleDeny}
                  disabled={isProcessing}
                >
                  {t('auth:device.deny')}
                </Button>
                <Button
                  variant="ice"
                  className="flex-1"
                  onClick={handleApprove}
                  disabled={isProcessing}
                >
                  {t('auth:device.approve')}
                </Button>
              </div>
              </>
              )}
            </div>
          ) : (
            // Success screen with code
            <div className="space-y-6">
              {/* Success message */}
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('auth:device.successHeading')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('auth:device.successInstructions')}
                </p>
              </div>

              {/* Authorization code */}
              <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-2">{t('auth:device.authCodeLabel')}</p>
                  <p className="text-3xl font-mono font-bold text-gray-900 tracking-wider">
                    {authCode}
                  </p>
                </div>
              </div>

              {/* Copy button */}
              <Button
                variant="ice"
                className="w-full"
                onClick={copyToClipboard}
              >
                {copied ? (
                  <>
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('auth:device.copied')}
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {t('auth:device.copy')}
                  </>
                )}
              </Button>

              {/* Instructions */}
              <Banner tone="note" title={t('auth:device.nextStepsTitle')}>
                <ol className="list-decimal list-inside space-y-1">
                  <li>{t('auth:device.nextSteps.step1')}</li>
                  <li>{t('auth:device.nextSteps.step2')}</li>
                  <li>{t('auth:device.nextSteps.step3')}</li>
                </ol>
              </Banner>

              {/* Done button */}
              <Button
                variant="quiet"
                className="w-full"
                onClick={() => navigate(getDashboardUrl())}
              >
                {t('auth:device.backToDashboard')}
              </Button>
            </div>
          )}
        </Card>

        <p className="text-center text-sm text-gray-500 mt-4">
          <button onClick={() => navigate(getDashboardUrl())} className="hover:text-gray-700">{t('auth:device.backLink')}</button>
        </p>
      </div>
      </div>
      <MarketingFooter />
    </div>
  );
}
